<template>
  <Teleport to="body">
    <div v-show="!showBarcodeScanner && !showManualProduct" class="fixed inset-0 z-[120] flex items-end justify-center" @click.self="$emit('close')">
      <div class="absolute inset-0 bg-black/50 backdrop-blur-[2px]" @click="$emit('close')" />

      <div class="relative w-full max-w-[430px] bg-cream rounded-t-3xl shadow-2xl flex flex-col sheet-inset"
           style="height: 75vh">

        <!-- Заголовок -->
        <div class="px-5 pt-5 pb-3 shrink-0 flex items-center justify-between">
          <h2 style="font-family: Inter, sans-serif; font-size: var(--fs-18); font-weight: 700; color: var(--ink);">{{ title }}</h2>
          <button @click="$emit('close')" class="w-8 h-8 flex items-center justify-center rounded-full text-ink active:bg-line transition-colors">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <!-- Поиск -->
        <div class="px-5 pb-3 shrink-0">
          <div class="relative">
            <input
              ref="searchInput"
              v-model="query"
              type="text"
              placeholder="Поиск продукта"
              class="w-full h-12 pl-4 rounded-2xl bg-white text-ink text-16
                     font-medium outline-none"
              :class="query ? 'pr-10' : 'pr-[5.5rem]'"
              autocomplete="off"
              autocorrect="off"
              spellcheck="false"
              @input="onInput"
            />
            <!-- Когда есть текст — крестик очистки -->
            <button
              v-if="query"
              @click="query = ''; results = []"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-ink/60"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
            <!-- Когда нет текста — иконки добавления нового продукта и штрихкода -->
            <div v-else class="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
              <button
                @click="showManualProduct = true"
                class="w-10 h-10 flex items-center justify-center rounded-xl text-ink active:bg-[#E0D9CF] transition-colors"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              </button>
              <button
                @click="showBarcodeScanner = true"
                class="w-10 h-10 flex items-center justify-center rounded-xl text-ink active:bg-[#E0D9CF] transition-colors"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
                  <path d="M2 4h2v16H2zM6 4h1v16H6zM10 4h2v16h-2zM15 4h1v16h-1zM19 4h3v16h-3z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Результаты -->
        <div class="flex-1 overflow-y-auto px-5 pb-5">
          <div v-if="searching" class="flex justify-center pt-8">
            <div class="w-5 h-5 border-2 border-sand border-t-[#8B7B6B] rounded-full animate-spin" />
          </div>

          <div v-else-if="results.length > 0" class="flex flex-col gap-2">
            <button
              v-for="p in results"
              :key="p.id"
              @click="selectProduct(p)"
              class="w-full bg-white rounded-2xl px-3 py-3 text-left active:scale-[0.98] transition-transform"
            >
              <p class="text-15 font-normal text-ink leading-snug">{{ p.name }}</p>
              <div class="flex items-end justify-between mt-1.5">
                <div class="flex flex-wrap gap-1.5">
                  <span class="pill-kcal text-12 font-medium">{{ fmtVal(p.kcal) }} ккал</span>
                  <span class="pill-protein text-12 font-medium">{{ fmtVal(p.protein) }} г</span>
                  <span class="pill-fat text-12 font-medium">{{ fmtVal(p.fat) }} г</span>
                  <span class="pill-carbs text-12 font-medium">{{ fmtVal(p.carbs) }} г</span>
                </div>
                <span v-if="sourceBadge(p)" class="text-11 font-medium shrink-0 ml-2" :style="{ color: sourceBadge(p).color }">
                  ✓ {{ sourceBadge(p).label }}
                </span>
              </div>
            </button>
          </div>

          <div v-else-if="query.trim() && !searching" class="text-center pt-12">
            <p style="font-family: Inter, sans-serif; font-weight: 600; font-size: var(--fs-15); color: var(--ink);">По запросу «{{ query }}» продукты не найдены</p>
            <button
              @click="showManualProduct = true"
              class="mt-3 px-6 py-3 rounded-2xl text-16 font-semibold"
              style="background: #41291A; color: var(--cream); font-family: Inter, sans-serif;"
            >Добавить новый продукт</button>
          </div>

          <div v-else class="text-center pt-12">
            <p class="text-16 text-ink/70">Введите название продукта</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Диалог ручного добавления нового продукта -->
    <LabelReviewDialog
      v-if="showManualProduct"
      v-model="showManualProduct"
      :show-barcode-scanner="true"
      :start-in-review="true"
      @submitted="onManualProductSubmitted"
    />

    <!-- Сканер штрихкода (selectOnly — возвращает продукт, не добавляет в дневник) -->
    <BarcodeScannerDialog
      v-if="showBarcodeScanner"
      v-model="showBarcodeScanner"
      :select-only="true"
      @select="onBarcodeSelect"
    />
  </Teleport>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import api from '../services/api.js'
import LabelReviewDialog from './LabelReviewDialog.vue'
import BarcodeScannerDialog from './BarcodeScannerDialog.vue'

const props = defineProps({
  initialQuery: { type: String, default: '' },
  title: { type: String, default: 'Заменить продукт' },
})

const emit = defineEmits(['select', 'close'])

const query = ref(props.initialQuery)
const results = ref([])
const searching = ref(false)
const searchInput = ref(null)
const showManualProduct = ref(false)
const showBarcodeScanner = ref(false)
let debounceTimer = null

onMounted(() => {
  nextTick(() => searchInput.value?.focus())
  if (query.value.trim()) search()
})

function onInput() {
  clearTimeout(debounceTimer)
  if (!query.value.trim()) { results.value = []; return }
  debounceTimer = setTimeout(search, 300)
}

async function search() {
  searching.value = true
  try {
    const { data } = await api.get('/products/search', {
      params: { q: query.value, limit: 40 },
    })
    results.value = data.products || []
  } catch {
    results.value = []
  } finally {
    searching.value = false
  }
}

function selectProduct(p) {
  emit('select', p)
}

function onManualProductSubmitted(result) {
  showManualProduct.value = false
  const p = result.product
  if (p?.id) {
    emit('select', {
      id: p.id,
      name: p.name,
      kcal: p.kcal ?? p.nutrients?.kcal ?? null,
      protein: p.protein ?? p.nutrients?.protein ?? null,
      fat: p.fat ?? p.nutrients?.fat ?? null,
      carbs: p.carbs ?? p.nutrients?.carbs ?? null,
      fiber: p.fiber ?? p.nutrients?.fiber ?? null,
      water: p.water ?? p.nutrients?.water ?? null,
      source: p.source || 'user',
      is_own: true,
    })
  }
}

function onBarcodeSelect(product) {
  showBarcodeScanner.value = false
  if (product?.id) {
    emit('select', product)
  }
}

function fmtVal(val) {
  if (val === null || val === undefined) return '0'
  const n = parseFloat(val)
  if (isNaN(n)) return '0'
  return n % 1 === 0 ? String(n) : n.toFixed(1)
}

function sourceBadge(p) {
  const s = (p.source || '').toLowerCase()
  if (s.includes('скурихин') || s.includes('skurikhin')) return { label: 'Скурихин', color: '#2D6A4F' }
  if (s.includes('usda')) return { label: 'USDA', color: '#1A5A7A' }
  if (p.is_own) return { label: 'Добавлено мной', color: '#7A6B5D' }
  return null
}
</script>

<style scoped>
.pill-kcal    { background: #EE998C; border-radius: 8px; padding: 2px 8px; color: var(--ink); }
.pill-protein { background: #9FC2C1; border-radius: 8px; padding: 2px 8px; color: var(--ink); }
.pill-fat     { background: #EBC06D; border-radius: 8px; padding: 2px 8px; color: var(--ink); }
.pill-carbs   { background: #EEAD77; border-radius: 8px; padding: 2px 8px; color: var(--ink); }
</style>
